import type {
  AysClinicSize,
} from '@bindoc-projects/clinic-domain-lib';
import {
  AYS_CLINIC_SIZE_DISPLAY_NAMES,
} from '@bindoc-projects/clinic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const AYS_CLINIC_SIZE_CONDITION_ID = 'clinicSize';
export const FilterConditionClinicSizesPluginType: FilterConditionPluginSpecification<
  AysClinicSize[],
  FilterConditionClinicSizesDto,
  AysClinicSizeCondition
> = {
  type: 'clinicSizes',
  category: 'clinic',
  displayName: { de: 'Klinik-Größe' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionClinicSizesDto,
  ): FilterConditionPluginImpl<AysClinicSize[]> {
    return new FilterConditionClinicSizesPlugin(obj.clinicSizes);
  },
  fromCondition(
    jsonObj: AysClinicSizeCondition,
  ): FilterConditionPluginImpl<AysClinicSize[]> {
    return new FilterConditionClinicSizesPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<AysClinicSize[]> {
    return new FilterConditionClinicSizesPlugin([]);
  },
};

export class FilterConditionClinicSizesPlugin extends FilterConditionPluginImpl<
  AysClinicSize[],
  FilterConditionClinicSizesDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    AysClinicSize[],
    FilterConditionClinicSizesDto,
    AysClinicSizeCondition
  > = FilterConditionClinicSizesPluginType;

  constructor(private clinicSizes: AysClinicSize[]) {
    super(FilterConditionClinicSizesPluginType.type);
  }

  clone(): FilterConditionClinicSizesPlugin {
    return new FilterConditionClinicSizesPlugin([...this.clinicSizes]);
  }

  override equals(other: FilterConditionClinicSizesPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.clinicSizes, other.clinicSizes)
    );
  }

  override validationToText(): string {
    if (this.clinicSizes.length === 0) {
      return 'Mindestens eine Klinik-Größe auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new AysClinicSizeCondition(this.clinicSizes, []);
  }

  toJSON(): FilterConditionClinicSizesDto | null {
    return {
      type: this.type,
      clinicSizes: this.clinicSizes,
    };
  }

  toText(): string | null {
    return this.clinicSizes.length === 1
      ? 'eine Klinik-Größe'
      : `${this.clinicSizes.length} Klinik-Größen`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.clinicSizes.map(
      (size) => AYS_CLINIC_SIZE_DISPLAY_NAMES[size] ?? String(size),
    );
    return { title, items };
  }

  toValue(): AysClinicSize[] {
    return this.clinicSizes;
  }
}

export class AysClinicSizeCondition extends BdContentCondition<AysClinicSize> {
  constructor(includes: AysClinicSize[], excludes: AysClinicSize[]) {
    super(AYS_CLINIC_SIZE_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionClinicSizesDto {
  type: string;
  clinicSizes: number[];
}
